// Push Notification Utility for Affirmation Reminders

export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    console.log("This browser does not support notifications");
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  return false;
};

export const sendNotification = (title, options = {}) => {
  if (Notification.permission === "granted") {
    const notification = new Notification(title, {
      icon: "/Subheading.png",
      badge: "/Subheading.png",
      vibrate: [200, 100, 200],
      requireInteraction: true,
      ...options
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    return notification;
  }
  return null;
};

// Store scheduled reminder times in localStorage for persistence
const REMINDER_STORAGE_KEY = 'lalaland_reminder_settings';
const LAST_REMINDER_KEY = 'lalaland_last_reminder';

export const saveReminderSettings = (settings) => {
  localStorage.setItem(REMINDER_STORAGE_KEY, JSON.stringify(settings));
};

export const loadReminderSettings = () => {
  try {
    const stored = localStorage.getItem(REMINDER_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Check if current time matches any reminder time (within 1 minute window)
// Also check if we haven't already sent a reminder in the last minute
const shouldTriggerReminder = (times) => {
  const now = new Date();
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  
  // Check if time matches
  if (!times.includes(currentTime)) {
    return false;
  }
  
  // Check if we already sent a reminder this minute
  const lastReminder = localStorage.getItem(LAST_REMINDER_KEY);
  if (lastReminder === currentTime) {
    return false;
  }
  
  // Save this minute as the last reminder time
  localStorage.setItem(LAST_REMINDER_KEY, currentTime);
  return true;
};

// Default affirmations for notifications
const defaultAffirmations = [
  "I am worthy of love, abundance, and all the blessings the universe has for me.",
  "I radiate positive energy and attract amazing opportunities.",
  "I am grateful for this beautiful day and all its possibilities.",
  "I trust the journey and know that everything is unfolding perfectly.",
  "I am strong, capable, and ready to embrace whatever comes my way.",
  "I choose peace, joy, and love in every moment.",
  "I am exactly where I need to be on my spiritual journey.",
  "I release what no longer serves me and welcome positive change.",
  "My heart is open to giving and receiving love.",
  "I am connected to the infinite wisdom of the universe."
];

// Initialize the reminder checker that runs every 30 seconds for more accuracy
let reminderIntervalId = null;

export const initializeReminderChecker = () => {
  // Clear any existing interval
  if (reminderIntervalId) {
    clearInterval(reminderIntervalId);
  }

  // Check immediately on init
  checkAndSendReminder();

  // Then check every 30 seconds for better accuracy
  reminderIntervalId = setInterval(() => {
    checkAndSendReminder();
  }, 30000); // Check every 30 seconds

  console.log("🔔 Reminder checker initialized");
  return reminderIntervalId;
};

const checkAndSendReminder = () => {
  const settings = loadReminderSettings();
  
  if (!settings || !settings.enabled || !settings.times?.length) {
    return;
  }

  if (Notification.permission !== "granted") {
    return;
  }

  if (shouldTriggerReminder(settings.times)) {
    const randomAffirmation = defaultAffirmations[Math.floor(Math.random() * defaultAffirmations.length)];
    sendNotification("✨ Daily Affirmation", {
      body: randomAffirmation,
      tag: `affirmation-${Date.now()}`,
      renotify: true
    });
    console.log("🔔 Reminder sent:", randomAffirmation);
  }
};

export const stopReminderChecker = () => {
  if (reminderIntervalId) {
    clearInterval(reminderIntervalId);
    reminderIntervalId = null;
  }
};

// Schedule notifications based on saved reminder times (legacy - kept for compatibility)
export const scheduleReminders = (times, affirmations, enabled) => {
  // Save settings to localStorage for the checker
  saveReminderSettings({ times, enabled, categories: [] });
  
  if (!enabled || !times?.length) return [];

  // Initialize the minute-by-minute checker
  initializeReminderChecker();
  
  return [];
};

// Clear all scheduled reminders
export const clearReminders = (scheduledIds) => {
  stopReminderChecker();
};

// Check if notifications are supported and permission status
export const getNotificationStatus = () => {
  if (!("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission;
};

// Check if reminder checker is active
export const isReminderCheckerActive = () => {
  return reminderIntervalId !== null;
};
